/**
 * Short numeric dates for results dashboards (own + team, comparisons, charts).
 * Fixed format: dd.mm.yyyy (zero-padded day and month, dot separator).
 */

/**
 * date-fns format strings for Chart.js time scales (chartjs-adapter-date-fns).
 */
export const CHARTJS_DATE_DISPLAY_FORMAT_DAY = 'dd.MM.yyyy';
export const CHARTJS_DATE_DISPLAY_FORMAT_MONTH = 'MM.yyyy';

export const CHARTJS_TIME_SCALE_DISPLAY_FORMATS = {
  day: CHARTJS_DATE_DISPLAY_FORMAT_DAY,
  week: CHARTJS_DATE_DISPLAY_FORMAT_DAY,
  month: CHARTJS_DATE_DISPLAY_FORMAT_MONTH,
};

/**
 * Chart.js time-scale x-axis config for results line charts.
 * @param {'daily'|'weekly'|'monthly'|string} [granularity]
 * @returns {object}
 */
export function buildChartJsTimeScaleConfig(granularity) {
  let timeUnit = 'week';
  if (granularity === 'daily') timeUnit = 'day';
  else if (granularity === 'monthly') timeUnit = 'month';
  return {
    type: 'time',
    time: {
      unit: timeUnit,
      isoWeekday: true,
      displayFormats: CHARTJS_TIME_SCALE_DISPLAY_FORMATS,
    },
    reverse: false,
  };
}

/**
 * @param {string[]} timeKeys
 * @param {Array<number|null|undefined>} values
 * @returns {Array<{ x: string, y: number }>}
 */
export function mapChartValuesToTimeScalePoints(timeKeys, values) {
  return timeKeys
    .map((timeKey, index) => {
      const y = values?.[index];
      if (y == null || Number.isNaN(Number(y))) return null;
      return { x: timeKey, y: Number(y) };
    })
    .filter((point) => point != null);
}

/**
 * Category-scale line charts may use ISO date strings as labels.
 * @param {unknown} label
 * @returns {boolean}
 */
export function isIsoDateChartLabel(label) {
  if (label == null || label === '') return false;
  return /^\d{4}-\d{2}-\d{2}/.test(String(label));
}

/**
 * Map vue-i18n locale codes to BCP-47 for date formatting.
 * Used by long-date formatting; short dates use fixed dd.mm.yyyy.
 * @param {string} [i18nLocale]
 * @returns {string}
 */
export function resolveLocaleForDateFormat(i18nLocale) {
  if (!i18nLocale || i18nLocale === 'en') return 'en-GB';
  return i18nLocale;
}

/**
 * @param {Date} date
 * @returns {string}
 */
function formatDateAsDdMmYyyy(date) {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

export const PRIMEVUE_DATE_FORMAT = 'dd.mm.yy';
export const LOCALE_DATE_PLACEHOLDER = 'dd.mm.yyyy';
export const LOCALE_DATETIME_PLACEHOLDER = 'dd.mm.yyyy HH:mm';

/**
 * Parse yyyy-mm-dd to a local-midnight Date (avoids UTC shift from `new Date('yyyy-mm-dd')`).
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseIsoDateOnly(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * @param {string|number|Date|null|undefined} value
 * @returns {string} yyyy-mm-dd or empty string
 */
export function toIsoDateOnly(value) {
  const d = parseDateValue(value);
  if (!d) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Map PrimeVue DatePicker value to yyyy-mm-dd string (empty when cleared).
 * @param {Date|null|undefined} value
 * @returns {string}
 */
export function isoDateFromCalendarValue(value) {
  return value ? toIsoDateOnly(value) : '';
}

/**
 * @param {string|number|Date|null|undefined} value
 * @returns {Date|null}
 */
function parseDateValue(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/**
 * @param {string|number|Date|null|undefined} value
 * @param {string} [_i18nLocale] Kept for call-site compatibility; short dates are locale-independent.
 * @returns {string}
 */
export function formatLocaleDate(value, _i18nLocale) {
  const d = parseDateValue(value);
  if (!d) return '';
  return formatDateAsDdMmYyyy(d);
}

/**
 * @param {string|number|Date|null|undefined} value
 * @returns {string}
 */
export function formatLocaleTime(value) {
  const d = parseDateValue(value);
  if (!d) return '';
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * @param {string|number|Date|null|undefined} value
 * @returns {string}
 */
export function formatLocaleDateTime(value) {
  const d = parseDateValue(value);
  if (!d) return '';
  return `${formatDateAsDdMmYyyy(d)} ${formatLocaleTime(d)}`;
}

/** Long month name, e.g. DE "02. März 2026", EN-GB "02 March 2026" */
const LOCALE_DATE_LONG_OPTIONS = {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
};

/**
 * @param {string|number|Date|null|undefined} value
 * @param {string} [i18nLocale]
 * @returns {string}
 */
export function formatLocaleDateLong(value, i18nLocale) {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(
    resolveLocaleForDateFormat(i18nLocale),
    LOCALE_DATE_LONG_OPTIONS,
  );
}
